
import { Card } from '@/components/ui/card';
import { SignUpForm } from '@/components/auth/SignUpForm';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useAuth } from '@/lib/auth';
import { getActiveCampaigns } from '@/config/campaigns';
import type { Campaign } from '@/config/campaigns';
import { Sparkles } from 'lucide-react';

const SignUpPage = () => {
  const navigate = useNavigate();
  const { user, loading, initialized } = useAuth();
  const [searchParams] = useSearchParams();
  const [activeCampaign, setActiveCampaign] = useState<Campaign | null>(null);

  // Check for campaign parameter in URL
  useEffect(() => {
    const campaignParam = searchParams.get('campaign');
    if (campaignParam) {
      const campaigns = getActiveCampaigns();
      const matchingCampaign = campaigns.find(c => c.id === campaignParam || c.metadata.campaign === campaignParam);
      if (matchingCampaign) {
        setActiveCampaign(matchingCampaign);
      }
    }
  }, [searchParams]);

  useEffect(() => {
    // Only log significant auth state changes to reduce console spam
    if (user && initialized && !loading) {
      console.log("[SignUpPage] User detected, but letting AuthRedirect handle the routing", {
        timestamp: new Date().toISOString()
      });
      // Don't redirect here - let AuthRedirect component handle the onboarding flow
    }
  }, [user, loading, initialized, navigate]);

  // Show loading if auth is still initializing
  if (loading || !initialized) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Checking authentication...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="flex flex-col gap-4 p-6 md:p-10">
        <div className="flex justify-center gap-2 md:justify-start">
          <Link to="/" className="flex items-center gap-2 font-medium">
            <img 
              src="/assets/icons/branding/chronocal-logo.svg" 
              alt="ChronoCal Logo" 
              className="h-6 w-auto" 
            />
          </Link>
        </div>
        
        <div className="flex flex-1 items-center justify-center">
          <div className="w-full max-w-sm space-y-6">
            {/* Campaign Promotion Banner - Above signup form */}
            {activeCampaign && (
              <div className="bg-gradient-to-r from-purple-600 via-pink-600 to-orange-600 text-white rounded-lg p-3 shadow-lg">
                <div className="flex items-center justify-center gap-2 mb-1.5">
                  <Sparkles className="h-4 w-4 text-yellow-300 animate-pulse" />
                  <span className="font-black text-xs uppercase tracking-wide">
                    Limited Time Offer
                  </span>
                  <Sparkles className="h-4 w-4 text-yellow-300 animate-pulse" />
                </div>
                <div className="text-center">
                  <div className="flex items-center justify-center gap-2 mb-1">
                    <span className="bg-red-500 px-2 py-0.5 rounded font-black text-xs">
                      {activeCampaign.discount.amount}% OFF
                    </span>
                    <span className="line-through text-white/70 text-sm">
                      ${activeCampaign.pricing.original}
                    </span>
                    <span className="text-yellow-300 font-bold text-lg">
                      ${activeCampaign.pricing.discounted.toFixed(2)}
                    </span>
                  </div>
                  <p className="text-xs text-white/90">
                    Save ${(activeCampaign.pricing.original - activeCampaign.pricing.discounted).toFixed(2)} • Expires Dec 5th
                  </p>
                </div>
              </div>
            )}
            
            <SignUpForm />
          </div>
        </div>
      </div>
      <div className="relative hidden bg-muted lg:block">
        <img
          src="/assets/images/ui/4dcfe676-520b-42e3-99c5-f61446691dc1.png"
          alt="ChronoCal DNA Calendar"
          className="absolute inset-0 h-full w-full object-cover"
        />
      </div>
    </div>
  );
};

export default SignUpPage;
